'use client'

import type { ComponentType } from 'react'
import { cn } from '@/lib/utils'

/**
 * A console-style mode switch: a recessed track with a glowing lime thumb
 * that slides under the active option. Used for Visual / Technical and
 * Map / List on the character-select section.
 */
export function ConsoleToggle<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string
  value: T
  options: { value: T; label: string; icon: ComponentType<{ className?: string }> }[]
  onChange: (value: T) => void
  className?: string
}) {
  const index = Math.max(0, options.findIndex((option) => option.value === value))

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span className="hidden font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground sm:inline">{label}</span>
      <div
        role="group"
        aria-label={label}
        className="relative inline-grid rounded-full bg-background p-1 shadow-[inset_0_2px_10px] shadow-background ring-1 ring-foreground/15"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        <span
          aria-hidden
          className="absolute inset-y-1 left-1 rounded-full bg-gradient-to-b from-primary to-primary/80 shadow-[0_0_22px] shadow-primary/45 ring-1 ring-foreground/30 transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{ width: `calc((100% - 0.5rem) / ${options.length})`, transform: `translateX(${index * 100}%)` }}
        />
        {options.map((option) => {
          const active = option.value === value
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(option.value)}
              className={cn(
                'relative z-10 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 font-heading text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-5',
                active ? 'text-primary-foreground' : 'text-foreground/70 hover:text-foreground',
              )}
            >
              <option.icon className="h-4 w-4" />
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
